import { Stack } from '@/components/UI/Stack';
import React, { useMemo, useEffect, useState } from 'react';
import { useBlock } from '@/hooks/useBlock';
import { IconFont } from '@/components/IconFont';
import { Button } from '@/components/UI/Button';
import { Tooltip } from '@/components/UI/Tooltip';
import { t, BasicType, createBlockDataByType, JsonToMjml } from '@bay-email/core';
import { useFocusIdx } from '@/hooks/useFocusIdx';
import { useEditorProps } from '@/hooks/useEditorProps';
import mjml from 'mjml-browser';
import './index.scss';

// Email Size Calculator Component
const EmailSizeCalculator: React.FC = () => {
	const { blockMode } = useEditorProps();
	const { values } = useBlock();
	const [sizeInKB, setSizeInKB] = useState<number>(0);

	useEffect(() => {
		try {
			// Generate MJML from current content
			const mjmlString = JsonToMjml({
				data: values.content,
				mode: 'production',
				context: values.content,
			});

			// Convert MJML to HTML
			const { html } = mjml(mjmlString, { validationLevel: 'soft' });

			if (html) {
				// Calculate the size in bytes using Blob
				const sizeInBytes = new Blob([html]).size;
				const sizeInKB = sizeInBytes / 1024;
				setSizeInKB(sizeInKB);
			} else {
				setSizeInKB(0);
			}
		} catch (err) {
			console.warn('[EmailSize] Failed to calculate size:', err);
			setSizeInKB(0);
		}
	}, [values.content]);

	// Determine color and status based on size thresholds
	const getSizeStatus = (size: number) => {
		if (size > 85) {
			return { status: 'critical', color: '#f53f3f', bgColor: '#ffece8' }; // Red
		} else if (size > 50) {
			return { status: 'warning', color: '#ff7d00', bgColor: '#fff7e6' }; // Orange
		} else if (size >= 15) {
			return { status: 'optimal', color: '#00b42a', bgColor: '#e8f5e8' }; // Green
		} else {
			return { status: 'small', color: '#4e5969', bgColor: '#f2f3f5' }; // Gray (default)
		}
	};

	const sizeStatus = getSizeStatus(sizeInKB);
	const tooltipContent = (
		<div>
			<div style={{ marginBottom: '4px' }}>
				<strong>Optimal email size: 15-100 KB</strong>
			</div>
			<div>• 15-50 KB: Excellent</div>
			<div>• 50-85 KB: Good, but watch your size</div>
			<div>• 85 KB+: May be clipped by email providers</div>
		</div>
	);

	const sizeDisplay = (
		<div
			className='email-size-display'
			style={{
				backgroundColor: sizeStatus.bgColor,
				color: sizeStatus.color,
			}}
		>
			<span className='email-size-label'>
				{blockMode ? 'Block Size:' : 'Email Size:'}
			</span>
			<span
				className='email-size-value'
				style={{ color: sizeStatus.color }}
			>
				{sizeInKB.toFixed(1)} KB
			</span>
		</div>
	);

	return (
		<>
			<Tooltip
				content={tooltipContent}
				placement='bottom'
			>
				{sizeDisplay}
			</Tooltip>
		</>
	);
};

// Breadcrumb Component
const NavigationBreadcrumb: React.FC = () => {
	const { focusIdx } = useFocusIdx();
	const { values } = useBlock();
	const { blockMode } = useEditorProps();

	// Helper function to safely get nested values
	function getValueByPath(obj: any, path: string) {
		return path.split('.').reduce((current, key) => current?.[key], obj);
	}

	const breadcrumbItems = useMemo(() => {
		if (!focusIdx || focusIdx === 'content') {
			return [];
		}

		// Parse the focusIdx path (e.g., "content.children.0.children.1")
		const pathParts = focusIdx.split('.');
		const items: Array<{ label: string; path: string }> = [];

		// Build breadcrumb based on path depth
		let currentPath = 'content';
		for (let i = 1; i < pathParts.length; i += 2) {
			if (pathParts[i] === 'children' && pathParts[i + 1]) {
				currentPath += `.${pathParts[i]}.${pathParts[i + 1]}`;

				// Get the block at this path to determine its type
				const block = values && getValueByPath(values, currentPath);
				if (block) {
					const blockType = block.type;
					let label = 'Content';

					// Map block types to friendly names
					switch (blockType) {
						case 'section':
							label = 'Section';
							break;
						case 'column':
							label = 'Column';
							break;
						case 'wrapper':
							label = 'Wrapper';
							break;
						case 'hero':
							label = 'Hero';
							break;
						case 'text':
							label = 'Text';
							break;
						case 'button':
							label = 'Button';
							break;
						case 'image':
							label = 'Image';
							break;
						default:
							label = blockType.charAt(0).toUpperCase() + blockType.slice(1);
					}

					items.push({ label, path: currentPath });
				}
			}
		}

		// Block editor variant: the master Page and the fixed root Wrapper are not
		// user-navigable levels, so drop the leading Wrapper crumb — the block
		// designer only navigates the content INSIDE the wrapper.
		if (blockMode && items[0]?.path === 'content.children.0') {
			return items.slice(1);
		}

		return items;
	}, [focusIdx, values, blockMode]);

	return (
		<div className='tools-panel-breadcrumb'>
			{breadcrumbItems.map((item, index) => (
				<React.Fragment key={item.path}>
					{index > 0 && <span className='breadcrumb-separator'>{'>'}</span>}
					<span
						className={`breadcrumb-item ${
							index === breadcrumbItems.length - 1 ? 'active' : ''
						}`}
					>
						{item.label}
					</span>
				</React.Fragment>
			))}
		</div>
	);
};

export function ToolsPanel() {
	const { redo, undo, redoable, undoable, change } = useBlock();

	const handleClearAll = () => {
		const confirmed = window.confirm(
			t('Are you sure you want to clear all content? This action cannot be undone.'),
		);
		if (confirmed) {
			// Create a fresh empty page instead of resetting to initial state
			const emptyPage = createBlockDataByType(BasicType.PAGE, {
				children: [],
			});
			change('content', emptyPage);
		}
	};

	return (
		<div className='tools-panel'>
			{/* Left section - breadcrumb */}
			<div className='tools-panel__left'>
				<NavigationBreadcrumb />
			</div>

			{/* Right section - Email Size and Tools */}
			<div className='tools-panel__right'>
				<EmailSizeCalculator />
				<Stack
					spacing='tight'
					alignment='center'
				>
					<Tooltip content={t('Undo')}>
						<span style={{ display: 'inline-flex' }}>
							<Button
								disabled={!undoable}
								onClick={undo}
								noBorder
							>
								<IconFont
									iconName='icon-undo'
									style={{
										cursor: 'inherit',
										opacity: undoable ? 1 : 0.75,
									}}
								/>
							</Button>
						</span>
					</Tooltip>

					<Tooltip content={t('Redo')}>
						<span style={{ display: 'inline-flex' }}>
							<Button
								disabled={!redoable}
								onClick={redo}
								noBorder
							>
								<IconFont
									iconName='icon-redo'
									style={{
										cursor: 'inherit',
										opacity: redoable ? 1 : 0.75,
									}}
								/>
							</Button>
						</span>
					</Tooltip>

					<Tooltip content={t('Clear All')}>
						<Button
							onClick={handleClearAll}
							noBorder
						>
							<IconFont
								iconName='icon-delete'
								style={{
									cursor: 'inherit',
								}}
							/>
						</Button>
					</Tooltip>
				</Stack>
			</div>
		</div>
	);
}
